import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import GenericNav from '../../../components/genericcomponents/navbars/GenericNav'
import GenericSearch from '../../../components/GenericSearch/generic-search'
import { getReports } from '../../../api/reporting/get-reports'
import ReportsByCategory from './components/ReportsByCategory/reports-by-category'
import GenericSnackbar from '../../../components/GenericSnackbar/generic-snackbar'
import {
  Box,
  Backdrop,
  Container,
  CircularProgress,
  Grid,
  Typography,
} from '../../../components/mui/Core/index'
import useBrowserTabTitles from '../../../hooks/browserTabTitles/browser-tab-titles'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import PageTitle from '../../../components/PageTitle/page-title'
import Constants from '../../../constants/en.json'
import SelectClientDropdown from '../../../components/dropdowns/SelectClientDropdown/select-client-dropdown'
import { hasUserPermission } from '../../../utils/user-utils'
import { userPermissionConstants } from '../../../constants/user-permission-constants'
import ClientSelection from '../../../components/ClientSelection/client-selection'
import { sessionStorageKeyConstants } from '../../../constants/session-storage-key-constants'
import ReportsLoadingSkeleton from './components/ReportsLoadingSkeleton/ReportsLoadingSkeleton'
import NoResultsFoundTableOverlay from '../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'

function Reports() {
  const dispatch = useDispatch()
  const [searchVal, setSearchVal] = useState('')
  const [reports, setReports] = useState([])
  const [categoryList, setCategoryList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isNoSearchResult, setIsNoSearchResult] = useState(false)
  const [selectedClient, setSelectedClient] = useState(
    JSON.parse(sessionStorage.getItem(sessionStorageKeyConstants.SELECTED_CLIENT)),
  )
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const hasSwitchClientPermission = hasUserPermission({
    permissionCode: userPermissionConstants.SWITCH_CLIENT,
    permissions: authenticatedUser?.permissions,
  })
  const browserTabTitle = selectedClient?.clientShortName
    ? `${selectedClient?.clientShortName} - ${Constants.reports.REPORTS}`
    : Constants.reports.REPORTS
  useBrowserTabTitles(browserTabTitle)

  useEffect(() => {
    if (!hasSwitchClientPermission || (hasSwitchClientPermission && selectedClient)) {
      setIsLoading(true)
      getReports()
        .then((response) => {
          setIsLoading(false)
          if (response.data.data) {
            setReports(response.data.data)
          }
        })
        .catch((exception) => {
          setIsLoading(false)
          dispatch(
            setSnackbar({
              message: exception?.message,
              severity: 'error',
              action: true,
            }),
          )
        })
    }
  }, [dispatch, hasSwitchClientPermission, selectedClient])

  useEffect(() => {
    const categories = []
    reports.forEach((data) =>
      categories.push({
        category: data.reportCategory,
        title: data.reportCategoryReadable,
      }),
    )
    const reducedList = categories.filter(
      (ele, ind) => ind === categories.findIndex((elem) => elem.category === ele.category),
    )
    setCategoryList(reducedList)
  }, [reports])

  const returnSearch = (inputValue) => {
    setSearchVal(inputValue)
  }

  const handleSelectClientDropdown = (client) => {
    setSelectedClient(client)
    returnSearch('') // Reset the table filter
  }

  const handleSearchResult = (value = false) => {
    setIsNoSearchResult(value)
  }

  const searchStyle = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'right',
    marginTop: '30px',
  }

  const searchFloatingStyle = {
    display: 'flex',
    position: 'relative',
    justifyContent: 'right',
    top: '75px',
  }
  const searchContainerStyle = hasSwitchClientPermission ? searchStyle : searchFloatingStyle
  const clientSelectionContainerStyle = { mt: 3 }
  const noReportsToViewContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    mt: '280px',
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <GenericNav selected='Reports' user='authenticated' />
      <Backdrop open={isLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Container maxWidth='xl'>
        <Grid container size={{ xs: 12 }}>
          <Grid size={{ xs: hasSwitchClientPermission ? 12 : 6 }}>
            <PageTitle title={Constants.reports.REPORTS} />
          </Grid>
          {hasSwitchClientPermission &&
            (!selectedClient ? (
              <Grid sx={clientSelectionContainerStyle} size={{ xs: 6 }}>
                <ClientSelection
                  handleSelectOption={handleSelectClientDropdown}
                  message={Constants.reports.CLIENT_SELECTION_MESSAGE}
                />
              </Grid>
            ) : (
              <Grid sx={clientSelectionContainerStyle} size={{ xs: 6 }}>
                <SelectClientDropdown
                  currentState={selectedClient}
                  handleSelectOption={handleSelectClientDropdown}
                />
              </Grid>
            ))}
          {((hasSwitchClientPermission && selectedClient && reports.length > 0) ||
            (!hasSwitchClientPermission && reports.length > 0)) && (
            <Grid size={{ md: 2.8, lg: 5.9 }} sx={searchContainerStyle}>
              <GenericSearch
                key={selectedClient?.clientCode || 'clientCode'}
                returnSearch={returnSearch}
              />
            </Grid>
          )}
          {isLoading && <ReportsLoadingSkeleton />}
          {searchVal && !isLoading && isNoSearchResult && (
            <Grid size={{ xs: 12 }} sx={{ mt: '80px' }}>
              <NoResultsFoundTableOverlay
                filterByDescription={Constants.reports.REPORTS}
                noResultsValue={searchVal}
              />
            </Grid>
          )}

          {((hasSwitchClientPermission && selectedClient && reports.length > 0) ||
            (!hasSwitchClientPermission && reports.length > 0)) &&
            !isLoading &&
            categoryList.map((data) => {
              return (
                <ReportsByCategory
                  key={data.category}
                  reports={reports}
                  category={data.category}
                  title={data.title}
                  searchvalue={searchVal}
                  handleSearchResult={handleSearchResult}
                />
              )
            })}
          {!isLoading &&
            reports.length === 0 &&
            (!hasSwitchClientPermission ||
              (hasSwitchClientPermission && selectedClient && (
                <Grid size={{ xs: 12 }} sx={noReportsToViewContainerStyle}>
                  <Typography variant='h6'>{Constants.reports.NO_REPORTS_TO_VIEW}</Typography>
                </Grid>
              )))}
        </Grid>
      </Container>
      <GenericSnackbar />
    </Box>
  )
}

export default Reports
